import type { Reservation } from '../store/useStore'

/**
 * #1371 無断キャンセル（no_show）で**実際にいくら請求できたか**を判定する。
 *
 * ## なぜ必要か
 *
 * 従来はモードに関係なく「キャンセル料請求済」と決め打ちで表示していた。
 * ところが当日払い（保証 OFF）は決済手段そのものを持たないため、
 * `_handle_visited_or_no_show` は何もせず return する。
 * **1 円も取れていないのに「請求済」と出て、店主が取れたと誤認していた。**
 *
 * 判定は「決済手段があるか」で決まる:
 *
 * | モード | paymentIntentId | 実際に取れる額 |
 * |---|---|---|
 * | 前払い | あり（automatic capture 済） | 全額 |
 * | 手付金 | あり（手付金のみ capture 済） | 手付金のみ。残額は請求手段なし |
 * | 当日払い + 保証 ON | あり（manual capture の与信） | 与信額を capture |
 * | 当日払い + 保証 OFF | **なし** | **ゼロ**。記録のみ |
 */

export type NoShowOutcome =
  /** 全額請求できた（前払い / 与信 capture） */
  | { kind: 'charged'; amount: number }
  /** 一部だけ請求できた（手付金モード。残額は請求手段なし） */
  | { kind: 'partial'; amount: number; unpaid: number }
  /** 請求手段が無い（当日払い + 保証 OFF）。記録のみ */
  | { kind: 'none' }

export function noShowOutcome(r: Reservation): NoShowOutcome {
  // 決済手段が無い = 請求のしようがない。ここを取り違えると誤認表示に戻る。
  if (!r.paymentIntentId) return { kind: 'none' }

  const total = r.totalAmount ?? 0

  // 手付金モードは手付金分しか決済されていない。残額は当日 QR で回収する想定
  // なので、来店しなかった以上は取れない。
  if (r.paymentTiming === 'deposit') {
    const paid = r.depositAmount ?? 0
    return { kind: 'partial', amount: paid, unpaid: Math.max(0, total - paid) }
  }

  // 前払い（決済済）/ 当日払い + 保証 ON（与信を capture）はいずれも全額
  return { kind: 'charged', amount: total }
}
